import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { useToast } from "../../contexts/ToastContext";
import { useConfirmPasswordResetMutation } from "../../store/api/authApi";

const PasswordResetConfirmPage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const { success, error: showError } = useToast();
	const [confirmReset, { isLoading }] = useConfirmPasswordResetMutation();

	const [formData, setFormData] = useState({
		password: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [showPassword, setShowPassword] = useState(false);

	// Redirect if no token
	useEffect(() => {
		if (!token) {
			showError("Invalid reset link. Please request a new password reset.");
			navigate("/password-reset", { replace: true });
		}
	}, [token, navigate, showError]);

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.password) {
			newErrors.password = "Password is required";
		} else if (formData.password.length < 8) {
			newErrors.password = "Password must be at least 8 characters long";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrors({});

		if (!token) {
			showError("Invalid reset link. Please request a new password reset.");
			navigate("/password-reset", { replace: true });
			return;
		}

		if (!validate()) {
			return;
		}

		try {
			await confirmReset({
				token,
				password: formData.password,
			}).unwrap();

			success(
				"Password reset successfully! You can now login with your new password.",
			);
			navigate("/login", { replace: true });
		} catch (err: any) {
			console.error("Password reset confirm failed:", err);

			if (err?.data?.error) {
				showError(err.data.error);
				setErrors({ submit: err.data.error });
			} else if (err?.data?.message) {
				showError(err.data.message);
				setErrors({ submit: err.data.message });
			} else if (err?.data?.detail) {
				showError(err.data.detail);
				setErrors({ submit: err.data.detail });
			} else {
				const errorMessage = "Failed to reset password. Please try again.";
				showError(errorMessage);
				setErrors({ submit: errorMessage });
			}
		}
	};

	if (!token) {
		return null; // Will redirect in useEffect
	}

	return (
		<div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background p-4">
			<div className="absolute inset-0 opacity-5">
				<div
					className="absolute inset-0"
					style={{
						backgroundImage:
							"url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
					}}
				/>
			</div>

			<div className="relative w-full max-w-md">
				<div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8">
					<div className="absolute -right-16 -top-16 h-32 w-32 rounded-full bg-primary/5" />
					<div className="absolute -bottom-12 -left-12 h-24 w-24 rounded-full bg-accent/5" />

					<div className="relative z-10 space-y-6">
						<div className="text-center">
							<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
								<Icon name="KeyRound" size={20} />
							</div>
							<h1 className="text-2xl font-semibold text-foreground">
								Reset Your Password
							</h1>
							<p className="mt-2 text-sm text-muted-foreground">
								Enter your new password below
							</p>
						</div>

						<form onSubmit={handleSubmit} className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									New Password
								</label>
								<div className="relative">
									<Input
										type={showPassword ? "text" : "password"}
										name="password"
										value={formData.password}
										onChange={(e) =>
											handleInputChange("password", e.target.value)
										}
										placeholder="Enter your new password"
										required
										error={errors.password}
										className="pr-10"
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
										aria-label={
											showPassword ? "Hide password" : "Show password"
										}
									>
										<Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
									</button>
								</div>
								{formData.password &&
									formData.password.length < 8 &&
									!errors.password && (
										<p className="mt-1 text-xs text-muted-foreground">
											Password must be at least 8 characters long
										</p>
									)}
							</div>

							{errors.submit && (
								<div className="rounded-lg bg-destructive/10 p-3 text-center text-sm text-destructive">
									{errors.submit}
								</div>
							)}

							<Button type="submit" className="w-full" disabled={isLoading}>
								{isLoading ? (
									<>
										<Icon
											name="Loader2"
											size={16}
											className="mr-2 animate-spin"
										/>
										Resetting Password...
									</>
								) : (
									<>
										<Icon name="Key" size={16} className="mr-2" />
										Reset Password
									</>
								)}
							</Button>
						</form>

						<div className="text-center">
							<button
								type="button"
								className="text-sm text-muted-foreground transition-colors hover:text-primary"
								onClick={() => navigate("/login")}
							>
								← Back to Login
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PasswordResetConfirmPage;
